import React, { forwardRef, useRef, useEffect, useState } from 'react';
import { Volume2, BookOpen, Pin, Download, Share2, Star } from 'lucide-react';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from './firebase';
import starPng from './assets/star.png';
import { themes } from './themes';
import { translations } from './lang';

// Internal component that handles the actual rendering
const CardInternal = ({ data, theme, lang, isGhost = false, onDownload, onShare, starSrc }) => {
    const currentTheme = themes[theme] || themes['yellow'];
    const t = translations[lang] || translations['en'];

    const headerRef = useRef(null);
    const containerRef = useRef(null);
    const [fontSize, setFontSize] = useState(null);
    const [likes, setLikes] = useState(data?.likes || []);

    useEffect(() => {
        setLikes(data?.likes || []);
    }, [data?.likes]);

    const handleLike = async () => {
        if (!auth.currentUser) {
            alert(t?.loginToLike || "Kelimeyi beğenmek için lütfen giriş yapın.");
            return;
        }
        
        const userEmail = auth.currentUser.email;
        const isLiked = likes.includes(userEmail);
        
        const newLikes = isLiked 
            ? likes.filter(email => email !== userEmail) 
            : [...likes, userEmail];
            
        setLikes(newLikes);
        
        try {
            await updateDoc(doc(db, "words", data.id), {
                likes: isLiked ? arrayRemove(userEmail) : arrayUnion(userEmail)
            });
        } catch (error) {
            console.error("Error updating like:", error);
            setLikes(likes);
            alert("Beğeni işlemi başarısız oldu.");
        }
    };

    useEffect(() => {
        const adjustFontSize = () => {
            const element = headerRef.current;
            const container = containerRef.current;
            if (!element || !container) return;

            // Determine dimensions based on container or ghost state
            const containerWidth = isGhost ? 480 : container.offsetWidth;

            // Adjust logic: If width is small, behave like mobile.
            // 480px is a sort of "tablet/large phone" width. 
            // Standard mobile is < 768.
            // Let's use the container width to decide "base" size.
            const isSmall = containerWidth < 500;
            let currentSize = isSmall ? 44 : 72;
            const minSize = 20;

            // For Ghost card (480px), we want it to look "good". 
            // 480px allows for slightly larger text than 300px mobile.
            if (isGhost) currentSize = 56; // A specific starting point for our export size

            // Reset to max size to measure
            element.style.fontSize = `${currentSize}px`;
            element.style.whiteSpace = 'nowrap';

            while (element.scrollWidth > element.clientWidth && currentSize > minSize) {
                currentSize--;
                element.style.fontSize = `${currentSize}px`;
            }

            if (element.scrollWidth > element.clientWidth) {
                element.style.whiteSpace = 'normal';
                element.style.overflowWrap = 'break-word';
            } else {
                element.style.whiteSpace = 'nowrap';
            }

            setFontSize(currentSize);
        };

        adjustFontSize();

        // Use ResizeObserver for more robust resizing support
        const resizeObserver = new ResizeObserver(() => {
            adjustFontSize();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [data.word, isGhost]);


    const playAudio = () => {
        if (data?.phonetics) {
            const audioSrc = data.phonetics.find(p => p.audio)?.audio;
            if (audioSrc) {
                new Audio(audioSrc).play();
            } else {
                alert(t.noAudio);
            }
        }
    };

    if (!data) return null;

    return (
        <div ref={containerRef} className="w-full h-full">
            <div
                className={`
                relative w-full h-full p-8 flex flex-col
                border-4 border-black 
                ${currentTheme.bg} 
                shadow-[16px_16px_0px_0px_rgba(0,0,0,1)]
                transition-all duration-300
              `}
                // Inline style for pattern
                style={{
                    backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
                    backgroundSize: '16px 16px'
                }}
            >
                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-b-4 border-r-4 border-black bg-white z-10"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-t-4 border-l-4 border-black bg-white z-10"></div>

                {/* Header: Word & Phonetic */}
                <div className="flex-shrink-0">
                    <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                            {/* Only show download button on non-ghost card, but it triggers ghost download */}
                            {!isGhost && (
                                <div className="flex gap-1 items-center">
                                    <div className="flex items-center gap-1.5 mr-1">
                                        <button
                                            onClick={handleLike}
                                            className={`no-print p-1 hover:bg-gray-200 rounded-full border-2 border-black transition-colors flex items-center justify-center w-8 h-8 ${likes.includes(auth?.currentUser?.email) ? 'bg-[#FFDE59]' : 'bg-white'}`}
                                            title={t?.likeWord || "Beğen"}
                                        >
                                            <Star size={20} strokeWidth={2.5} className={likes.includes(auth?.currentUser?.email) ? 'fill-black' : ''} />
                                        </button>
                                        {likes.length > 0 && <span className="font-black text-sm">{likes.length}</span>}
                                    </div>
                                    <button
                                        onClick={onDownload}
                                        className="no-print p-1 bg-white hover:bg-gray-200 rounded-full border-2 border-black transition-colors"
                                        title={t.downloadCard}
                                    >
                                        <Download size={20} strokeWidth={2} />
                                    </button>
                                    <button
                                        onClick={onShare}
                                        className="no-print p-1 bg-white hover:bg-gray-200 rounded-full border-2 border-black transition-colors"
                                        title={t.shareCard}
                                    >
                                        <Share2 size={20} strokeWidth={2} />
                                    </button>
                                </div>
                            )}
                            {/* Ghost card placeholder to keep layout identical if needed, or just omit */}
                            {isGhost && <div className="w-8 h-8" />}
                        </div>
                        <div className="flex gap-1">
                            <StarCanvas src={starSrc} />
                        </div>
                    </div>

                    {data.pinned && (
                        <div className="absolute top-[-10px] right-[-10px] bg-[#FF66C4] border-2 border-black p-2 rounded-full z-20 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] rotate-12">
                            <Pin size={24} className="text-white" strokeWidth={3} />
                        </div>
                    )}

                    <h2
                        ref={headerRef}
                        className={`font-black tracking-tighter mb-2 ${currentTheme.text} ${fontSize ? '' : 'text-5xl md:text-7xl break-words'}`}
                        style={{
                            WebkitTextStroke: '1px black',
                            fontSize: fontSize ? `${fontSize}px` : undefined,
                            lineHeight: 1.1
                        }}
                    >
                        {data.word}
                    </h2>

                    <div className="flex items-center gap-3 mb-6">
                        {(data.phonetics && data.phonetics[0]?.text) && (
                            <span className="text-xl font-bold font-mono bg-white border-2 border-black px-3 py-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                                {data.phonetics[0].text}
                            </span>
                        )}
                        {data.phonetics && data.phonetics.find(p => p.audio) && (
                            <button
                                onClick={playAudio}
                                className="p-2 bg-black text-white hover:bg-gray-800 transition-colors border-2 border-black rounded-full"
                                title={t.playPronunciation}
                            >
                                <Volume2 size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Body: Meanings */}
                <div className="flex-grow overflow-y-auto flex flex-col gap-4 pr-2 custom-scrollbar max-h-[400px]">
                    {(() => {
                        const totalLength = data.meanings.reduce((acc, m) => acc + (m.definitions[0]?.definition?.length || 0), 0);
                        const fontSizeClass = totalLength > 300 ? 'text-[10px]' :
                            totalLength > 240 ? 'text-xs' :
                                totalLength > 180 ? 'text-sm' :
                                    totalLength > 120 ? 'text-base' : 'text-lg';

                        return data.meanings.map((meaning, idx) => (
                            <div key={idx} className="bg-white border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                                <div className="flex items-center gap-2 mb-2 border-b-2 border-black pb-1">
                                    <BookOpen size={16} strokeWidth={3} />
                                    <span className="font-black italic text-sm uppercase tracking-wider">
                                        {t.partOfSpeech?.[meaning.partOfSpeech?.toLowerCase()] || meaning.partOfSpeech}
                                    </span>
                                </div>
                                <p className={`font-bold ${fontSizeClass} leading-tight mb-2`}>
                                    {meaning.definitions[0].definition}
                                </p>
                                {meaning.definitions[0].example && (
                                    <p className="text-sm text-gray-600 font-mono italic border-l-4 border-gray-300 pl-2 mt-2">
                                        "{meaning.definitions[0].example}"
                                    </p>
                                )}
                            </div>
                        ));
                    })()}
                </div>

                {/* See Also Section */}
                {data.seeAlso && (
                    <div className="mt-4 pt-2 border-t-2 border-black border-dashed">
                        <span className="font-black text-[10px] uppercase tracking-widest block mb-1">
                            {t.seeAlsoLabel}:
                        </span>
                        <p className="font-bold text-md italic">
                            {data.seeAlso}
                        </p>
                    </div>
                )}

                {/* Footer */}
                <div className="mt-6 pt-4 border-t-4 border-black flex justify-between items-end">
                    <div className="flex flex-col">
                        <span className="font-black text-[10px] uppercase tracking-widest">{t.sourceLabel}</span>
                        <span className="font-bold text-xs truncate max-w-[150px]">{t.sourceValue}</span>
                    </div>
                    <div className={`px-4 py-1 border-2 border-black font-black text-sm uppercase ${currentTheme.accent} shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]`}>
                        {t.footerBrand}
                    </div>
                </div>

            </div>
        </div>
    );
};


// Helper component to render star on canvas for better export compatibility
const StarCanvas = ({ src }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !src) return;

        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = src;

        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
    }, [src]);

    return (
        <canvas
            ref={canvasRef}
            width={32}
            height={32}
            className="w-8 h-8 object-contain"
            style={{ mixBlendMode: 'normal' }}
        />
    );
};


const WordCard = forwardRef(({ data, theme = 'yellow', lang = 'en' }, ref) => {
    const t = translations[lang] || translations['en'];
    const ghostRef = useRef(null);
    const [starSrc, setStarSrc] = useState(starPng);

    useEffect(() => {
        // Preload star image as base64 to ensure dom-to-image captures it
        // This fixes the missing star on iOS/Safari share
        const img = new Image();
        img.src = starPng;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
                const dataURL = canvas.toDataURL('image/png');
                setStarSrc(dataURL);
            } catch (e) {
                console.warn("Could not convert star image to base64", e);
                // Fallback to original src if CORS/other issues prevent conversion
                setStarSrc(starPng);
            }
        };
    }, []);

    React.useImperativeHandle(ref, () => ({
        triggerDownload: handleDownload,
        generateCardBlob: generateCardRef
    }));

    const generateCardRef = async () => {
        if (!window.domtoimage || !ghostRef.current) return null;

        const node = ghostRef.current;
        const logicalWidth = 480;
        const logicalHeight = 600;
        // We can use a slightly lower scale for sharing if file size is an issue, 
        // but let's keep high quality for consistency.
        // For sharing, sometimes JPEG is smaller/better supported, but PNG is fine.
        const targetWidth = 1080;
        const targetHeight = 1350;
        const scale = targetHeight / logicalHeight;

        try {
            return await window.domtoimage.toBlob(node, {
                quality: 1,
                width: targetWidth,
                height: targetHeight,
                style: {
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${logicalWidth}px`,
                    height: `${logicalHeight}px`,
                },
                filter: (node) => !node.classList?.contains('no-print')
            });
        } catch (err) {
            console.error("Image generation failed:", err);
            return null;
        }
    };

    const handleShare = async () => {
        if (!navigator.share) {
            alert(t.shareNotSupported || "Sharing not supported on this device/browser.");
            return;
        }

        const blob = await generateCardRef();
        if (!blob) {
            alert(t.imageGenFailed);
            return;
        }

        const file = new File([blob], `dictionary-card-${data.word}.png`, { type: 'image/png' });

        try {
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: `Devrimci Sözlük: ${data.word}`,
                    text: `Check out this word: ${data.word}`,
                });
            } else {
                // Fallback if file sharing not explicitly supported but navigator.share exists
                // or just try sharing anyway as some implementations are fuzzy
                await navigator.share({
                    title: `Devrimci Sözlük: ${data.word}`,
                    text: `Check out this word: ${data.word}`,
                    // url: window.location.href // Optional: link to the app
                });
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error("Share failed:", err);
                alert(t.shareFailed || "Share failed.");
            }
        }
    }

    const handleDownload = async () => {
        if (!window.domtoimage || !ghostRef.current) {
            alert(t.imageGenNotReady);
            return;
        }

        const blob = await generateCardRef();
        if (!blob) {
            alert(t.downloadFailed);
            return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `dictionary-card-${data.word}.png`;
        link.href = url;
        link.click();

        // Clean up
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };

    if (!data) return null;

    return (
        <div
            ref={ref}
            className="p-2 md:p-4 bg-transparent w-full flex justify-center"
        >
            {/* 1. Visible, Responsive Card */}
            <div className="relative group perspective-1000 w-full max-w-[300px] sm:max-w-[400px]">
                <div className="w-[300px] sm:w-[400px] h-[600px] mx-auto">
                    <CardInternal
                        data={data}
                        theme={theme}
                        lang={lang}
                        isGhost={false}
                        onDownload={handleDownload}
                        onShare={handleShare}
                        starSrc={starSrc}
                    />
                </div>
                {/* Back decoration for depth effect on desktop */}
                <div className={`
                  hidden lg:block absolute top-0 left-0 w-full h-full -z-10
                  bg-black translate-x-4 translate-y-4
                  border-4 border-black
                  w-[300px] sm:w-[400px] h-[600px] mx-auto right-0
                `}></div>
            </div>

            {/* 2. Hidden "Ghost" Card for Export (Fixed 480x600) */}
            <div style={{ position: 'fixed', left: '-9999px', top: 0, width: '480px', height: '600px' }}>
                <div ref={ghostRef} style={{ width: '480px', height: '600px' }}>
                    <CardInternal
                        data={data}
                        theme={theme}
                        lang={lang}
                        isGhost={true}
                        starSrc={starSrc}
                    />
                </div>
            </div>
        </div>
    );
});

export default WordCard;
